import React from 'react'

import { safeGetLocalItem, safeSetLocalItem } from '@helpers'

import { useActiveCompany } from './useActiveCompany'

// A record rather than a list, so adding a service type fails to compile until it is handled here.
const SERVICE_TYPE_VALUES: Record<ServiceType, true> = { autotax: true, quickkeret: true }
const DEFAULT_SERVICE_TYPE: ServiceType = 'quickkeret'
export const LS_SERVICE_TYPE_NAME = 'tms_service_type'

function isServiceType(value: Nullable<string>): value is ServiceType {
  // Compared by value, not `in`: the latter also accepts inherited keys such as `toString`.
  return value != null && SERVICE_TYPE_VALUES[value as ServiceType] === true
}

function readPersistedServiceType(): Nullable<ServiceType> {
  const stored = safeGetLocalItem(LS_SERVICE_TYPE_NAME)
  return isServiceType(stored) ? stored : null
}

/** Logged-out chrome has no company in scope, so it can only go on the last resolved value. */
function getHintedServiceType(): ServiceType {
  return readPersistedServiceType() ?? DEFAULT_SERVICE_TYPE
}

/** The active company's service type. */
export function useServiceType(): ServiceType {
  const activeCompany = useActiveCompany()

  const serviceType = activeCompany ? (activeCompany.service_type ?? DEFAULT_SERVICE_TYPE) : getHintedServiceType()

  React.useEffect(() => {
    // Only a company-backed value is worth remembering; the fallbacks would just echo themselves.
    if (activeCompany) {
      safeSetLocalItem(LS_SERVICE_TYPE_NAME, serviceType)
    }
  }, [activeCompany, serviceType])

  return serviceType
}
